"use client";

import { useCallback, useEffect, useState } from "react";
import { css, cx } from "styled-system/css";
import { cf } from "@/components/brand/sfturnos-styles";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";

type PolicyResponse = {
  hoursBeforeCancel: number;
  penaltyType: "lose_deposit" | "no_penalty";
};

function exampleLine(hours: number): string {
  if (!Number.isFinite(hours) || hours <= 0) {
    return "Ejemplo: con 0 horas, la seña se reembolsa aunque se cancele minutos antes del turno.";
  }
  const now = new Date();
  const appt = new Date(now.getTime() + 24 * 3_600_000);
  appt.setHours(10, 0, 0, 0);
  const cutoff = new Date(appt.getTime() - hours * 3_600_000);
  const fmt = (d: Date) => d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
  return `Ejemplo: si el turno es a las ${fmt(appt)}, el reembolso aplica si se cancela antes de las ${fmt(cutoff)}.`;
}

export function CancellationPolicyForm() {
  const { refresh } = useAuth();
  const [policy, setPolicy] = useState<PolicyResponse | null>(null);
  const [hoursInput, setHoursInput] = useState("24");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetchWithSessionSync("/api/dashboard/cancellation-policy", { credentials: "include" }, refresh);
    if (r.ok) {
      const j = (await r.json()) as PolicyResponse;
      setPolicy(j);
      setHoursInput(String(j.hoursBeforeCancel));
    }
    setLoading(false);
  }, [refresh]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(next: Partial<{ penaltyType: "lose_deposit" | "no_penalty"; hoursBeforeCancel: number }>) {
    setSaving(true);
    setMsg(null);
    const r = await fetchWithSessionSync(
      "/api/dashboard/cancellation-policy",
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(next),
      },
      refresh,
    );
    setSaving(false);
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      setMsg(j.error ?? "No se pudo guardar.");
      return;
    }
    const j = (await r.json()) as PolicyResponse;
    setPolicy(j);
    setHoursInput(String(j.hoursBeforeCancel));
  }

  const enabled = policy?.penaltyType === "lose_deposit";
  const parsedHours = Math.max(0, Math.min(168, Math.round(Number(hoursInput))));

  return (
    <div className={cx(cf.dashSurface, css({ p: "6", mb: "10" }))}>
      <h2 className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a", mb: "1" })}>
        Política de cancelación de señas
      </h2>
      <p className={css({ fontSize: "sm", color: "#64748b", mb: "4", lineHeight: "1.6", maxW: "44rem" })}>
        Define si las señas se devuelven automáticamente al cancelar un turno, según cuánta anticipación hubo. Aplica
        sin importar quién cancele (vos o el paciente desde su link).
      </p>

      {loading || !policy ? (
        <p className={css({ fontSize: "sm", color: "#64748b" })}>Cargando…</p>
      ) : (
        <div className={css({ display: "flex", flexDir: "column", gap: "3" })}>
          <label
            className={css({
              display: "flex",
              alignItems: "flex-start",
              gap: "2.5",
              cursor: saving ? "wait" : "pointer",
            })}
          >
            <input
              type="checkbox"
              className={css({ mt: "0.5" })}
              checked={enabled}
              disabled={saving}
              onChange={(e) => void save({ penaltyType: e.target.checked ? "lose_deposit" : "no_penalty" })}
            />
            <span>
              <span className={css({ fontSize: "sm", fontWeight: "medium", color: "#0f172a" })}>
                Seña reembolsable según anticipación
              </span>
              <span className={css({ display: "block", fontSize: "xs", color: "#64748b", mt: "0.5", lineHeight: "1.5" })}>
                Si está desactivado, la seña siempre se reembolsa al cancelar (comportamiento por defecto).
              </span>
            </span>
          </label>

          {enabled ? (
            <div
              className={css({
                ml: "6.5",
                pl: "4",
                borderLeftWidth: "2px",
                borderColor: "#e2e8f0",
                display: "flex",
                flexDir: "column",
                gap: "2",
              })}
            >
              <p className={css({ fontSize: "sm", color: "#334155", lineHeight: "1.6" })}>
                Si se cancela con más de{" "}
                <input
                  type="number"
                  min={0}
                  max={168}
                  step={1}
                  value={hoursInput}
                  disabled={saving}
                  onChange={(e) => setHoursInput(e.target.value)}
                  onBlur={() => {
                    if (Number.isFinite(parsedHours) && parsedHours !== policy.hoursBeforeCancel) {
                      void save({ hoursBeforeCancel: parsedHours });
                    } else {
                      setHoursInput(String(policy.hoursBeforeCancel));
                    }
                  }}
                  className={css({
                    w: "16",
                    mx: "1.5",
                    px: "2",
                    py: "1",
                    rounded: "md",
                    borderWidth: "1px",
                    borderColor: "#e2e8f0",
                    textAlign: "center",
                    fontSize: "sm",
                  })}
                />
                horas de anticipación → se reembolsa automáticamente. Si se cancela con menos, la seña queda en tu
                cuenta.
              </p>
              <p className={css({ fontSize: "xs", color: "#64748b", fontStyle: "italic" })}>
                {exampleLine(Number.isFinite(parsedHours) ? parsedHours : policy.hoursBeforeCancel)}
              </p>
            </div>
          ) : null}

          {msg ? <p className={css({ fontSize: "sm", color: "#b91c1c" })}>{msg}</p> : null}
        </div>
      )}
    </div>
  );
}
